<script setup lang="ts">
import type { TagsInputItemDeleteProps } from 'radix-vue'
import type { HTMLAttributes } from 'vue'
import { ChevronLeft, Ellipsis, Info, PencilLine, Pipette, Trash2, X } from 'lucide-vue-next'
import { TagsInputItemDelete, useForwardProps } from 'radix-vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { cn } from '~/helpers'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { getRandomColors } from '@/utils/colors'
import { getTagInfo } from '~~/src/api/tags'
import type { TagInfo } from '~~/src/api/tags/types'

const props = defineProps<TagsInputItemDeleteProps & {
  class?: HTMLAttributes['class']
  tagId: string
  color: string
  modelValue?: boolean
  elementalId?: string
}>()
const emit = defineEmits<{
  'colorChange': [color: string]
  'rename': []
  'delete': []
  'update:modelValue': [value: boolean]
  'removeFromElemental': []
}>()
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

const colorOptions = ref<string[]>([])
const isOpen = computed({
  get: () => props.modelValue ?? false,
  set: value => emit('update:modelValue', value),
})

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props
  return delegated
})

const forwardedProps = useForwardProps(delegatedProps)

const colorChangeActive = ref(false)
const tagInfoActive = ref(false)
const tagInfo = ref<TagInfo | null>(null)

const handleTagInfo = async () => {
  tagInfo.value = await getTagInfo(props.tagId)

  tagInfoActive.value = !tagInfoActive.value
  colorChangeActive.value = false
}

const handleColorClick = (e: Event) => {
  e.preventDefault()
  colorChangeActive.value = !colorChangeActive.value
}

const handleColorSelect = (color: string) => {
  emit('colorChange', color)
}
const showInputColor = ref(false)
const inputColor = ref(props.color)
const applyColor = () => {
  handleColorSelect(inputColor.value)
  showInputColor.value = false
}

const handleDelete = () => {
  emit('delete')
  isOpen.value = false
}

const handleRemoveFromElemental = () => {
  emit('removeFromElemental')
  isOpen.value = false
}

const handleRenameClick = () => {
  emit('rename')
  isOpen.value = false
}

onMounted(() => {
  colorOptions.value = Array.from({ length: 13 }, () => getRandomColors()[1])
})

onUnmounted(() => {
  colorChangeActive.value = false
  tagInfoActive.value = false
})
</script>

<template>
  <DropdownMenu v-model:open="isOpen">
    <DropdownMenuTrigger as-child>
      <TagsInputItemDelete v-bind="forwardedProps" :class="cn('flex rounded bg-transparent mr-1 opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-all backdrop-blur-md', props.class)" @click.stop>
        <slot>
          <Ellipsis class="w-4 h-4" />
        </slot>
      </TagsInputItemDelete>
    </DropdownMenuTrigger>
    <DropdownMenuContent class="min-w-38" :side-offset="5">
      <div v-if="!tagInfoActive">
        <DropdownMenuItem class="tags-input-item-options" @select.prevent="handleRenameClick">
          <PencilLine class="w-3 h-3 mr-2" />
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem class="tags-input-item-options" @select.prevent="handleDelete">
          <Trash2 class="w-3 h-3 mr-2" />
          Delete
        </DropdownMenuItem>
        <DropdownMenuItem class="tags-input-item-options" @select.prevent="handleTagInfo">
          <Info class="w-3 h-3 mr-2" />
          Tag info
        </DropdownMenuItem>
        <DropdownMenuItem class="tags-input-item-options" @select.prevent="handleColorClick">
          <Pipette class="w-3 h-3 mr-2" />
          Change color
        </DropdownMenuItem>
        <DropdownMenuItem v-if="isMobile && elementalId" class="tags-input-item-options" @select.prevent="handleRemoveFromElemental">
          <X class="w-3 h-3 mr-2" />
          Remove from elemental
        </DropdownMenuItem>
      </div>
      <div v-if="tagInfoActive">
        <div class="p-1">
          <div class="flex items-center mb-4">
            <ChevronLeft class="w-4 h-4 text-gray-400 cursor-pointer" @click="handleTagInfo" />
            <h2 class="flex-1 text-center text-12px font-semibold text-gray-400">
              Tag Info
            </h2>
          </div>
          <div class="space-y-4 text-12px">
            <div class="flex">
              <div class="w-32 text-gray-400">
                Creator
              </div>
              <div class="flex items-center space-x-2">
                <img :src="tagInfo?.user?.avatar" alt="Avatar" class="w-6 h-6 rounded-full">
                <span class="text-gray-900 font-medium">{{ tagInfo?.user?.name }}</span>
              </div>
            </div>

            <div class="flex justify-between">
              <div class="w-32 text-gray-400">
                Date Created
              </div>
              <div class="text-gray-900 font-medium">
                {{ new Date(tagInfo?.createdAt ?? '').toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) }}
              </div>
            </div>

            <div class="flex justify-between pt-1 pb-2">
              <div class="w-32 text-gray-400">
                Total Items
              </div>
              <div class="text-gray-900 font-medium">
                {{ tagInfo?.itemsCount }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="colorChangeActive" class="absolute bottom-0 left-0 top-full mt-2 min-w-38">
        <div class="rounded-md border bg-popover p-1 text-popover-foreground shadow-md p-1">
          <div class="grid grid-cols-7 gap-1">
            <div
              v-for="(color, index) in colorOptions"
              :key="index"
              class="cursor-pointer w-4 h-4 rounded-full hover:outline hover:outline-1 hover:outline-gray-200"
              :style="{ backgroundColor: color }"
              @click="handleColorSelect(color); showInputColor = false"
            />
            <Pipette class="w-4 h-4 cursor-pointer" @click="showInputColor = !showInputColor" />
          </div>
          <div v-if="showInputColor" class="flex items-center justify-center mt-2">
            <label for="color-input" class="cursor-pointer w-8 h-8 rounded-full" :style="{ backgroundColor: inputColor }" />
            <input id="color-input" v-model="inputColor" type="color" class="w-[1px] h-[1px] opacity-0">
            <button class="ml-2 bg-gray-200 text-gray-900 px-2 py-1 text-12px rounded-md" @click="applyColor">
              Apply
            </button>
          </div>
        </div>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>

<style>
.tags-input-item-options {
  @apply text-12px;
}
</style>
